import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Lightning, GoogleLogo } from '@phosphor-icons/react';

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
function handleGoogleRegister() {
  var redirectUrl = window.location.origin + '/';
  window.location.href = 'https://auth.emergentagent.com/?redirect=' + encodeURIComponent(redirectUrl);
}

export default function RegisterPage() {
  const { user, register } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', confirmPassword: '', role: 'affiliate', company: '' });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      const dest = user.role === 'admin' ? '/admin' : user.role === 'advertiser' ? '/advertiser' : '/affiliate';
      navigate(dest);
    }
  }, [user, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (form.password.length < 6) { setError('Password must be at least 6 characters'); return; }
    if (form.password !== form.confirmPassword) { setError('Passwords do not match'); return; }
    setLoading(true);
    const result = await register(form.name, form.email, form.password, form.role, form.company);
    if (!result.success) setError(result.error);
    setLoading(false);
  };

  const update = (field) => (e) => setForm(prev => ({ ...prev, [field]: e.target.value }));

  return (
    <div className="min-h-screen bg-zinc-950 flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="w-14 h-14 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <Lightning size={28} weight="duotone" className="text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-white">Join Dynamic World</h1>
          <p className="text-sm text-zinc-400 mt-1">Start monetizing global traffic</p>
        </div>

        <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-6">
          <h2 className="font-heading text-xl font-semibold text-white mb-6">Create Account</h2>

          {error && (
            <div data-testid="register-error" className="mb-4 p-3 bg-red-500/10 border border-red-500/20 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label className="text-zinc-300 text-sm">Full Name</Label>
              <Input data-testid="register-name" value={form.name} onChange={update('name')} placeholder="John Doe" required className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder-zinc-500 focus:border-blue-500" />
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Email</Label>
              <Input data-testid="register-email" type="email" value={form.email} onChange={update('email')} placeholder="you@example.com" required className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder-zinc-500 focus:border-blue-500" />
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Password</Label>
              <Input data-testid="register-password" type="password" value={form.password} onChange={update('password')} placeholder="Min 6 characters" required minLength={6} className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder-zinc-500 focus:border-blue-500" />
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Confirm Password</Label>
              <Input data-testid="register-confirm-password" type="password" value={form.confirmPassword} onChange={update('confirmPassword')} placeholder="Re-enter password" required className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder-zinc-500 focus:border-blue-500" />
              {form.confirmPassword && form.password !== form.confirmPassword && (
                <p className="text-xs text-red-400 mt-1">Passwords do not match</p>
              )}
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">I am an</Label>
              <div className="flex gap-3 mt-2">
                <button
                  type="button"
                  data-testid="role-affiliate"
                  onClick={() => setForm(p => ({ ...p, role: 'affiliate' }))}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                    form.role === 'affiliate'
                      ? 'bg-blue-600/15 text-blue-400 border-blue-500/30'
                      : 'bg-zinc-950 text-zinc-400 border-zinc-800 hover:border-zinc-700'
                  }`}
                >
                  Affiliate
                </button>
                <button
                  type="button"
                  data-testid="role-advertiser"
                  onClick={() => setForm(p => ({ ...p, role: 'advertiser' }))}
                  className={`flex-1 py-2.5 rounded-lg text-sm font-medium border transition-all ${
                    form.role === 'advertiser'
                      ? 'bg-blue-600/15 text-blue-400 border-blue-500/30'
                      : 'bg-zinc-950 text-zinc-400 border-zinc-800 hover:border-zinc-700'
                  }`}
                >
                  Advertiser
                </button>
              </div>
            </div>
            <div>
              <Label className="text-zinc-300 text-sm">Company (Optional)</Label>
              <Input data-testid="register-company" value={form.company} onChange={update('company')} placeholder="Your company name" className="mt-1 bg-zinc-950 border-zinc-800 text-zinc-100 placeholder-zinc-500 focus:border-blue-500" />
            </div>
            <Button data-testid="register-submit" type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-medium">
              {loading ? 'Creating account...' : 'Create Account'}
            </Button>
          </form>

          <div className="relative my-5">
            <div className="absolute inset-0 flex items-center"><div className="w-full border-t border-zinc-800"></div></div>
            <div className="relative flex justify-center text-xs"><span className="bg-zinc-900 px-3 text-zinc-500">or continue with</span></div>
          </div>
          <Button
            data-testid="google-register-btn"
            type="button"
            onClick={handleGoogleRegister}
            className="w-full bg-zinc-800 hover:bg-zinc-700 text-zinc-200 border border-zinc-700 gap-2"
          >
            <GoogleLogo size={18} weight="bold" />
            Sign up with Google
          </Button>

          <p className="text-sm text-zinc-400 text-center mt-6">
            Already have an account?{' '}
            <Link to="/login" className="text-blue-400 hover:text-blue-300 transition-colors" data-testid="go-to-login">
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
}
